<script setup lang="ts">
import { watch, onUnmounted } from 'vue';
import Teleport from 'vue2-teleport';

const props = defineProps<{
  isOpen: boolean;
  ariaLabelledby?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

let keydownListener: ((e: KeyboardEvent) => void) | null = null;

function detachKeydown(): void {
  if (!keydownListener) {
    return;
  }

  window.removeEventListener('keydown', keydownListener);
  keydownListener = null;
}

function attachKeydown(): void {
  if (keydownListener) {
    return;
  }

  keydownListener = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') {
      return;
    }

    emit('close');
  };

  window.addEventListener('keydown', keydownListener);
}

watch(
  () => props.isOpen,
  (open) => {
    if (!open) {
      detachKeydown();
      return;
    }

    attachKeydown();
  },
  { immediate: true }
);

onUnmounted(() => {
  detachKeydown();
});

function onBackdropClick(): void {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="ariaLabelledby"
      class="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:min-w-0 tw:items-center tw:justify-center tw:overflow-y-auto tw:bg-[rgba(22,30,45,0.2)] tw:backdrop-blur-sm tw:p-3 tw:py-4 sm:tw:p-4 sm:tw:py-6"
      @click.self="onBackdropClick"
    >
      <slot />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped></style>
